import { Link } from "react-router-dom";
import { ArrowRight, Building2, Home as HomeIcon, TrendingUp, FileCheck, Wrench, BedDouble, Landmark, Castle, Calculator, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Layout from "@/components/Layout";

const dispositifsNeuf = [
  {
    id: "jeanbrun",
    title: "Jeanbrun (2026)",
    icon: TrendingUp,
    description: "Nouveau dispositif lancé en février 2026, remplaçant le Pinel, basé sur l'amortissement du bien immobilier.",
    avantages: [
      "Amortissement de 3% à 5,5% par an sur 80% du prix",
      "Cumulable avec le déficit foncier",
      "Pas de plafond de réduction d'impôt",
      "Applicable au neuf ET à l'ancien rénové",
    ],
    conditions: [
      "Engagement locatif minimum de 9 ans",
      "Loyers plafonnés selon le niveau choisi",
      "Logements collectifs uniquement (pas de maisons)",
      "Pour l'ancien: travaux ≥ 30% du prix, DPE A ou B",
    ],
    niveaux: [
      { nom: "Intermédiaire", decote: "-15 à -20%", tauxNeuf: "3,5%", tauxAncien: "3%" },
      { nom: "Social", decote: "-30%", tauxNeuf: "4,5%", tauxAncien: "3,5%" },
      { nom: "Très social", decote: "-45%", tauxNeuf: "5,5%", tauxAncien: "4%" },
    ],
    tauxReduction: "3% à 5,5%/an",
    duree: "9 ans minimum",
    plafond: "Aucun plafond",
  },
  {
    id: "pinel",
    title: "Pinel / Pinel+ ⚠️ FIN 31/12/2024",
    icon: Building2,
    description: "Le dispositif Pinel permet de bénéficier d'une réduction d'impôt pour l'acquisition d'un logement neuf destiné à la location. ATTENTION: Fin du dispositif le 31 décembre 2024.",
    avantages: [
      "Réduction d'impôt de 9% à 14% selon la durée d'engagement",
      "Engagement locatif de 6, 9 ou 12 ans",
      "Plafond d'investissement de 300 000€",
      "Possibilité de louer à ses ascendants/descendants",
    ],
    conditions: [
      "Logement neuf ou en VEFA",
      "Normes énergétiques RT 2012 / RE 2020",
      "Zones éligibles: A, A bis, B1",
      "Loyers et ressources des locataires plafonnés",
    ],
    tauxReduction: "9% à 14%",
    duree: "6 à 12 ans",
    plafond: "300 000 €",
    alert: "FIN DU DISPOSITIF: 31 DÉCEMBRE 2024",
  },
];

const dispositifsAncien = [
  {
    id: "denormandie",
    title: "Denormandie",
    icon: HomeIcon,
    description: "Le dispositif Denormandie encourage la rénovation de logements anciens dans les centres-villes. Prolongé jusqu'en 2027.",
    avantages: [
      "Réduction d'impôt de 12% à 21% selon la durée",
      "Applicable dans 245 villes éligibles",
      "Travaux représentant 25% minimum du coût total",
      "Dispositif prolongé jusqu'en 2027",
    ],
    conditions: [
      "Logement ancien nécessitant des travaux",
      "Travaux ≥ 25% du coût total de l'opération",
      "Villes du programme 'Action Cœur de Ville'",
      "Amélioration de la performance énergétique de 20 à 30%",
    ],
    tauxReduction: "12% à 21%",
    duree: "6 à 12 ans",
    plafond: "300 000 €",
  },
  {
    id: "locavantages",
    title: "Loc'Avantages",
    icon: FileCheck,
    description: "Dispositif géré par l'Anah offrant une réduction d'impôt en échange de loyers modérés via une convention.",
    avantages: [
      "Réduction jusqu'à 65% des loyers perçus",
      "Cumulable avec MaPrimeRénov' et aides Anah",
      "Convention de 6 ans renouvelable",
      "Trois niveaux de loyers au choix",
    ],
    conditions: [
      "Logement décent (normes de confort)",
      "DPE classé E ou mieux",
      "Convention signée avec l'Anah",
      "Location nue, résidence principale du locataire",
    ],
    niveaux: [
      { nom: "Loc1", decote: "-15%", reduction: "15-20%" },
      { nom: "Loc2", decote: "-30%", reduction: "35-40%" },
      { nom: "Loc3", decote: "-45%", reduction: "65%" },
    ],
    tauxReduction: "15% à 65%",
    duree: "6 ans minimum",
    plafond: "Sur loyers perçus",
  },
  {
    id: "deficit_foncier",
    title: "Déficit Foncier",
    icon: Wrench,
    description: "Mécanisme permettant de déduire les travaux de rénovation de votre revenu global imposable.",
    avantages: [
      "Déduction des travaux du revenu global",
      "Plafond annuel de 10 700€ (21 400€ avec travaux énergétiques)",
      "Report du déficit sur 10 ans",
      "Compatible avec d'autres dispositifs",
    ],
    conditions: [
      "Travaux d'entretien, réparation, amélioration",
      "Bien générant des revenus fonciers",
      "Location nue obligatoire",
      "Engagement de location 3 ans après imputation",
    ],
    tauxReduction: "Variable selon TMI",
    duree: "3 ans minimum",
    plafond: "10 700 € à 21 400 €/an",
  },
];

const dispositifsSpeciaux = [
  {
    id: "lmnp",
    title: "LMNP (Loueur Meublé Non Professionnel)",
    icon: BedDouble,
    description: "Statut fiscal avantageux pour la location de biens meublés, avec possibilité d'amortissement.",
    avantages: [
      "Amortissement du bien et du mobilier",
      "Imposition sur les revenus BIC",
      "Possibilité de créer un déficit reportable",
      "Récupération de la TVA (résidences services)",
    ],
    conditions: [
      "Location meublée selon liste réglementaire",
      "Recettes locatives < 23 000€/an ou < 50% des revenus",
      "Déclaration au greffe du tribunal de commerce",
      "Tenue d'une comptabilité (régime réel)",
    ],
    tauxReduction: "Amortissement 25-30 ans",
    duree: "Sans limite",
    plafond: "Aucun",
  },
  {
    id: "malraux",
    title: "Malraux",
    icon: Landmark,
    description: "Réduction d'impôt pour la restauration d'immeubles situés dans des secteurs sauvegardés ou protégés.",
    avantages: [
      "Réduction de 22% à 30% des travaux",
      "Plafond élevé de 400 000€ sur 4 ans",
      "Non soumis au plafonnement des niches fiscales",
      "Valorisation patrimoniale importante",
    ],
    conditions: [
      "Immeuble en secteur sauvegardé ou ZPPAUP",
      "Restauration complète de l'immeuble",
      "Suivi par un Architecte des Bâtiments de France",
      "Engagement de location 9 ans",
    ],
    tauxReduction: "22% à 30%",
    duree: "9 ans",
    plafond: "400 000 € travaux",
  },
  {
    id: "monuments_historiques",
    title: "Monuments Historiques",
    icon: Castle,
    description: "Déduction intégrale des travaux sur les monuments classés ou inscrits, sans plafond.",
    avantages: [
      "Déduction de 100% des travaux sans plafond",
      "Imputation sur le revenu global",
      "Transmission facilitée (exonération partielle)",
      "Prestige et valorisation patrimoniale",
    ],
    conditions: [
      "Monument classé ou inscrit à l'inventaire",
      "Travaux autorisés par la DRAC",
      "Conservation du bien pendant 15 ans minimum",
      "Ouverture au public possible",
    ],
    tauxReduction: "100% des travaux",
    duree: "15 ans conservation",
    plafond: "Aucun",
  },
];

const DispositifCard = ({ dispositif }) => {
  const Icon = dispositif.icon;
  
  return (
    <Card className={`dispositif-card h-full ${dispositif.alert ? 'border-red-300' : ''}`} id={dispositif.id} data-testid={`dispositif-detail-${dispositif.id}`}>
      {dispositif.alert && (
        <div className="bg-red-600 text-white text-center py-2 text-sm font-semibold flex items-center justify-center gap-2">
          <AlertTriangle size={16} />
          {dispositif.alert}
        </div>
      )}
      <CardHeader className="pb-4">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-md ${dispositif.alert ? 'bg-red-100 text-red-600' : 'bg-harbor/10 text-harbor'}`}>
            <Icon size={28} />
          </div>
          <div>
            <CardTitle className="font-heading text-xl text-harbor">{dispositif.title}</CardTitle>
            <p className="text-muted-foreground text-sm mt-1">{dispositif.description}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Key metrics */}
        <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-md">
          <div className="text-center">
            <div className="text-lg font-semibold text-harbor">{dispositif.tauxReduction}</div>
            <div className="text-xs text-muted-foreground">Réduction</div>
          </div>
          <div className="text-center border-x border-gray-200">
            <div className="text-lg font-semibold text-harbor">{dispositif.duree}</div>
            <div className="text-xs text-muted-foreground">Durée</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-harbor">{dispositif.plafond}</div>
            <div className="text-xs text-muted-foreground">Plafond</div>
          </div>
        </div>

        {/* Avantages */}
        <div>
          <h4 className="font-semibold text-sm text-harbor mb-3">Avantages</h4>
          <ul className="space-y-2">
            {dispositif.avantages.map((avantage, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-growth mt-2 flex-shrink-0" />
                <span className="text-muted-foreground">{avantage}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Conditions */}
        <div>
          <h4 className="font-semibold text-sm text-harbor mb-3">Conditions</h4>
          <ul className="space-y-2">
            {dispositif.conditions.map((condition, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-clay mt-2 flex-shrink-0" />
                <span className="text-muted-foreground">{condition}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Niveaux (for Jeanbrun and Loc'Avantages) */}
        {dispositif.niveaux && (
          <div className="border-t pt-4">
            <h4 className="font-semibold text-sm text-harbor mb-3">Niveaux de réduction</h4>
            <div className="space-y-2">
              {dispositif.niveaux.map((niveau, index) => (
                <div key={index} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                  <span className="font-medium">{niveau.nom}</span>
                  <span className="text-muted-foreground">Décote: {niveau.decote}</span>
                  <span className="text-growth font-semibold">
                    {niveau.reduction || `${niveau.tauxNeuf} (neuf)`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Link to="/simulateur" className="block">
          <Button className="w-full bg-accent hover:bg-accent/90 text-white" data-testid={`simuler-${dispositif.id}`}>
            Simuler ce dispositif
            <Calculator className="ml-2" size={18} />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

const Dispositifs = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-harbor py-20" data-testid="dispositifs-hero">
        <div className="container-custom">
          <div className="max-w-3xl text-white">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4 animate-fade-in-up">
              Dispositifs de Défiscalisation Immobilière
            </h1>
            <p className="text-lg text-white/80 animate-fade-in-up animation-delay-200">
              Découvrez tous les dispositifs fiscaux permettant de réduire vos impôts
              grâce à l'investissement immobilier. Notre équipe vous accompagne pour
              choisir le dispositif le plus adapté à votre situation.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-spacing bg-paper" data-testid="dispositifs-content">
        <div className="container-custom">
          <Tabs defaultValue="ancien" className="w-full">
            <TabsList className="grid w-full max-w-lg mx-auto grid-cols-3 mb-12" data-testid="dispositifs-tabs">
              <TabsTrigger value="neuf" data-testid="tab-neuf">Immobilier Neuf</TabsTrigger>
              <TabsTrigger value="ancien" data-testid="tab-ancien">Immobilier Ancien</TabsTrigger>
              <TabsTrigger value="speciaux" data-testid="tab-speciaux">Dispositifs Spéciaux</TabsTrigger>
            </TabsList>

            <TabsContent value="neuf" className="animate-fade-in">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {dispositifsNeuf.map((dispositif) => (
                  <DispositifCard key={dispositif.id} dispositif={dispositif} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="ancien" className="animate-fade-in">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {dispositifsAncien.map((dispositif) => (
                  <DispositifCard key={dispositif.id} dispositif={dispositif} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="speciaux" className="animate-fade-in">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {dispositifsSpeciaux.map((dispositif) => (
                  <DispositifCard key={dispositif.id} dispositif={dispositif} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-growth/10" data-testid="dispositifs-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-harbor mb-4">
            Besoin d'aide pour choisir ?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Nos experts analysent votre situation fiscale et patrimoniale pour vous
            recommander le dispositif le plus avantageux.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/simulateur">
              <Button className="bg-accent hover:bg-accent/90 text-white" data-testid="dispositifs-simuler">
                Lancer une simulation
                <Calculator className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-harbor text-harbor hover:bg-harbor hover:text-white" data-testid="dispositifs-contact">
                Prendre rendez-vous
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Dispositifs;
